import React, { useState } from 'react';
import CaretLeftIcon from '../CustomIcons/CaretLeftIcon';
import CaretRightIcon from '../CustomIcons/CaretRightIcon';

export type TablePaginationProps = {
    current: number;
    pageSize: number;
    total: number;
    showSizeChanger?: boolean;
    showQuickJumper?: boolean;
    showTotal?: (total: number, range: [number, number]) => React.ReactNode;
    pageSizeOptions?: string[];
    onChange?: (page: number, pageSize: number) => void;
    onShowSizeChange?: (current: number, size: number) => void;
};

const baseButtonStyle: React.CSSProperties = {
    minWidth: 32,
    height: 32,
    padding: '0 8px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 8,
    color: 'var(--malachite-text-primary, rgb(10, 10, 10))',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
};

const arrowStyle = (disabled: boolean): React.CSSProperties => ({
    ...baseButtonStyle,
    width: 32,
    minWidth: 32,
    color: disabled
        ? 'var(--malachite-text-placeholder, rgb(187, 187, 187))'
        : 'var(--malachite-text-primary, rgb(10, 10, 10))',
    opacity: disabled ? 0.4 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
});

const pageButtonStyle = (active: boolean): React.CSSProperties => ({
    ...baseButtonStyle,
    border: active
        ? '1px solid var(--malachite-accent-border, rgba(0, 175, 102, 0.2))'
        : '1px solid transparent',
    color: active
        ? 'var(--malachite-accent-foreground, rgb(2, 122, 66))'
        : 'var(--malachite-text-primary, rgb(10, 10, 10))',
    background: active ? 'var(--malachite-card-background, rgb(255, 255, 255))' : 'transparent',
});

const ellipsisStyle: React.CSSProperties = {
    ...baseButtonStyle,
    color: 'var(--malachite-text-secondary, rgb(123, 123, 123))',
    cursor: 'default',
    letterSpacing: 1,
};

const selectStyle: React.CSSProperties = {
    height: 32,
    padding: '0 8px',
    border: '1px solid var(--malachite-card-border, rgb(232, 232, 232))',
    borderRadius: 8,
    background: 'var(--malachite-card-background, rgb(255, 255, 255))',
    color: 'var(--malachite-text-primary, rgb(10, 10, 10))',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundImage:
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M4 6l4 4 4-4' stroke='%230a0a0a' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    paddingRight: 28,
};

const jumperInputStyle: React.CSSProperties = {
    width: 56,
    height: 32,
    padding: '0 8px',
    border: '1px solid var(--malachite-card-border, rgb(232, 232, 232))',
    borderRadius: 8,
    background: 'var(--malachite-card-background, rgb(255, 255, 255))',
    color: 'var(--malachite-text-primary, rgb(10, 10, 10))',
    fontSize: 13,
    textAlign: 'center',
    outline: 'none',
};

const DEFAULT_PAGE_SIZE_OPTIONS = ['10', '25', '50', '100'];

/**
 * Compute the page numbers to render. Uses an ellipsis-aware window similar
 * to Ant Design's pagination: show first, last, and a small window around the
 * current page, separated by '...' placeholders where there are gaps.
 *
 * Returns an array of either page numbers or the string 'ellipsis'.
 */
const computePageList = (current: number, totalPages: number): Array<number | 'ellipsis'> => {
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const list: Array<number | 'ellipsis'> = [1];

    if (current <= 4) {
        list.push(2, 3, 4, 5, 'ellipsis', totalPages);
        return list;
    }

    if (current >= totalPages - 3) {
        list.push(
            'ellipsis',
            totalPages - 4,
            totalPages - 3,
            totalPages - 2,
            totalPages - 1,
            totalPages,
        );
        return list;
    }

    list.push('ellipsis', current - 1, current, current + 1, 'ellipsis', totalPages);
    return list;
};

const TablePagination: React.FC<TablePaginationProps> = ({
    current,
    pageSize,
    total,
    showSizeChanger = true,
    showQuickJumper = false,
    showTotal,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    onChange,
    onShowSizeChange,
}) => {
    // Guard pageSize <= 0: it would make totalPages Infinity/NaN and throw a
    // RangeError when building the page list. Fall back to a sane default.
    const safePageSize = pageSize > 0 ? pageSize : 10;
    const totalPages = Math.max(1, Math.ceil(total / safePageSize));
    const safeCurrent = Math.min(Math.max(1, current), totalPages);
    const [jumpValue, setJumpValue] = useState('');

    const goTo = (page: number) => {
        const next = Math.min(Math.max(1, page), totalPages);
        if (next !== safeCurrent) {
            onChange?.(next, safePageSize);
        }
    };

    const rangeStart = total === 0 ? 0 : (safeCurrent - 1) * safePageSize + 1;
    const rangeEnd = Math.min(safeCurrent * safePageSize, total);
    const totalLabel = showTotal
        ? showTotal(total, [rangeStart, rangeEnd])
        : `Total ${total.toLocaleString('en-US')} items`;

    const handleJump = () => {
        const n = parseInt(jumpValue, 10);
        if (!Number.isNaN(n)) {
            goTo(n);
            setJumpValue('');
        }
    };

    const pageList = computePageList(safeCurrent, totalPages);

    return (
        <div
            className="mt-4 flex items-center justify-end gap-3 flex-wrap"
            style={{ color: 'var(--malachite-text-primary, rgb(10, 10, 10))' }}
        >
            <span
                style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'var(--malachite-text-primary, rgb(10, 10, 10))',
                    marginRight: 4,
                }}
            >
                {totalLabel}
            </span>

            <div className="flex items-center gap-1">
                <button
                    type="button"
                    aria-label="Previous page"
                    onClick={() => goTo(safeCurrent - 1)}
                    disabled={safeCurrent <= 1}
                    style={arrowStyle(safeCurrent <= 1)}
                >
                    <CaretLeftIcon size={12} />
                </button>
                {pageList.map((entry, idx) =>
                    entry === 'ellipsis' ? (
                        <span key={`e-${idx}`} style={ellipsisStyle} aria-hidden="true">
                            •••
                        </span>
                    ) : (
                        <button
                            key={entry}
                            type="button"
                            aria-label={`Page ${entry}`}
                            aria-current={entry === safeCurrent ? 'page' : undefined}
                            onClick={() => goTo(entry)}
                            style={pageButtonStyle(entry === safeCurrent)}
                        >
                            {entry.toLocaleString('en-US')}
                        </button>
                    ),
                )}
                <button
                    type="button"
                    aria-label="Next page"
                    onClick={() => goTo(safeCurrent + 1)}
                    disabled={safeCurrent >= totalPages}
                    style={arrowStyle(safeCurrent >= totalPages)}
                >
                    <CaretRightIcon size={12} />
                </button>
            </div>

            {showSizeChanger && (
                <select
                    aria-label="Page size"
                    value={String(pageSize)}
                    onChange={(e) => {
                        const next = parseInt(e.target.value, 10);
                        if (!Number.isNaN(next)) {
                            onShowSizeChange?.(safeCurrent, next);
                            onChange?.(safeCurrent, next);
                        }
                    }}
                    style={selectStyle}
                >
                    {pageSizeOptions.map((sz) => (
                        <option key={sz} value={sz}>
                            {sz} / page
                        </option>
                    ))}
                </select>
            )}

            {showQuickJumper && (
                <div
                    className="flex items-center gap-2"
                    style={{
                        fontSize: 13,
                        color: 'var(--malachite-text-primary, rgb(10, 10, 10))',
                    }}
                >
                    <span>Go to</span>
                    <input
                        aria-label="Go to page"
                        type="number"
                        min={1}
                        max={totalPages}
                        value={jumpValue}
                        onChange={(e) => setJumpValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleJump();
                            }
                        }}
                        onBlur={handleJump}
                        style={jumperInputStyle}
                    />
                    <span>Page</span>
                </div>
            )}
        </div>
    );
};

export default TablePagination;
